import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, formatApiError } from "./AuthContext";
import { UploadCloud, Trash2, Copy, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMedia() {
  const { API, authHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/media`, { headers: authHeaders });
      setItems(data);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setError("");
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/admin/media`, fd, {
          headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
        });
      }
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      await axios.delete(`${API}/admin/media/${id}`, { headers: authHeaders });
      setItems((it) => it.filter((x) => x.id !== id));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    setTimeout(() => setCopied(null), 1600);
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary">Library</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-light">Media</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Upload images for essays and the home page. JPEG, PNG, or WebP up to 8&nbsp;MB.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground text-sm cursor-pointer hover:bg-primary/90">
          <UploadCloud size={16} strokeWidth={1.5} />
          {uploading ? "Uploading…" : "Upload images"}
          <input
            ref={fileRef}
            data-testid="media-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={onUpload}
            className="hidden"
          />
        </label>
      </div>

      {error && <p className="mb-6 text-sm italic font-serif text-destructive">{error}</p>}

      {items.length === 0 ? (
        <div className="bg-secondary/40 border border-dashed border-border rounded-3xl p-12 text-center">
          <p className="font-serif italic text-lg text-muted-foreground">
            The library is empty.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Upload your first image to begin.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((m) => {
            const fullUrl = `${BACKEND_URL}${m.url}`;
            return (
              <div
                key={m.id}
                data-testid={`media-${m.id}`}
                className="group bg-white/70 border border-border/60 rounded-3xl overflow-hidden"
              >
                <div className="aspect-[4/3] bg-secondary">
                  <img
                    src={fullUrl}
                    alt={m.filename}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <p className="text-sm truncate" title={m.filename}>
                    {m.filename}
                  </p>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <button
                      onClick={() => copyUrl(fullUrl)}
                      className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/70 hover:text-primary"
                    >
                      {copied === fullUrl ? (
                        <>
                          <Check size={14} strokeWidth={1.5} /> Copied
                        </>
                      ) : (
                        <>
                          <Copy size={14} strokeWidth={1.5} /> Copy URL
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => onDelete(m.id)}
                      className="p-2 text-foreground/60 hover:text-destructive"
                      title="Delete"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
